import { Link } from "react-router-dom";
import { useEffect } from "react";
import "@/App.css";

const IMAGES = {
  castle: "https://customer-assets.emergentagent.com/job_mobile-view-sync/artifacts/mpaoog5l_image.png",
  forest: "https://images.unsplash.com/photo-1726428755646-be185fe76a49?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjA0MTJ8MHwxfHNlYXJjaHwyfHxmb3Jlc3QlMjBuYXR1cmUlMjBwYXRofGVufDB8fHx8MTc3NjUwMTg2N3ww&ixlib=rb-4.1.0&q=85",
};

const TIMELINE = [
  { year: "17th C", event: "The Freke family arrives in West Cork and builds the original castle on a bluff overlooking the Atlantic." },
  { year: "1780", event: "The castle is substantially rebuilt and expanded in the Gothic Revival style, with battlements, towers, and courtyards." },
  { year: "1921", event: "The Evans-Freke family departs. The castle falls into disrepair over the following decades." },
  { year: "1950s", event: "Battlements stripped for their lead. Roofs collapse. Ivy and weather take their toll." },
  { year: "c. 2000", event: "Stephen Ralfe Evans-Freke, a retired Wall Street banker and direct descendant, buys back the castle at the turn of the millennium." },
  { year: "2004", event: "Restoration work begins in earnest. Rathbarry Castle, a nearby manor house, is also purchased." },
  { year: "2017+", event: "Master stuccodores begin the decorative plasterwork on the fan-vaulted ceilings of the music room and principal chambers." },
  { year: "Present", event: "Restoration continues. Over 40 chimneys relined, battlements repaired, extraordinary interior plasterwork completed. The castle extends to some 50,000 square feet." },
];

export default function CastlePage() {
  useEffect(() => { window.scrollTo(0, 0); }, []);

  return (
    <div className="article-wrap" data-testid="castle-page">
      <div className="article-hero" style={{ backgroundImage: `url(${IMAGES.castle})` }}>
        <div className="article-hero-overlay" />
        <div className="article-hero-content">
          <p className="article-back-hero"><Link to="/" data-testid="castle-back-link">&larr; Back to Castlefreke</Link></p>
          <div className="article-hero-meta">
            <span>Est. 1780</span>
            <span>Rathbarry, Co. Cork</span>
          </div>
          <h1 className="article-hero-title" data-testid="castle-title">The Restoration of<br />Castle Freke</h1>
          <p className="article-hero-author">A promise kept across centuries</p>
        </div>
      </div>

      <article className="article-container" data-testid="castle-body">
        <p className="article-lead">
          Built on a bluff above the Atlantic, with views stretching to the Fastnet Lighthouse on a clear day, Castle Freke is one of the most remarkable restoration stories in Ireland. After decades of neglect, the ancestral seat of the Evans-Freke family is being brought back to life by a descendant who made a promise &mdash; and kept it.
        </p>

        <h2 className="article-h2">A promise made over single malt</h2>
        <p className="article-p">
          Stephen Ralfe Evans-Freke grew up with an oil painting of Castle Freke in the playroom of his family home. The painting followed him to Cambridge, then to South Africa, then to Wall Street, where he became an investment banker. One night, after a large amount of single malt, he told a friend he was going to buy the house back.
        </p>
        <p className="article-p">
          It happened at a christening in London. The father of an Irish friend stomped over and told him the family pile was for sale. &ldquo;I gather you&rsquo;ve done well in the States,&rdquo; the man barked. &ldquo;What&rsquo;s the matter with you boy? You should be buying it back.&rdquo;
        </p>
        <p className="article-p">
          Back in Dublin, Evans-Freke says his feet &ldquo;turned of their own accord&rdquo; into an upmarket estate agency. On the eve of the millennium, he made good on that promise.
        </p>

        <h2 className="article-h2">The scale of the work</h2>
        <p className="article-p">
          The estate comprises two castles: Castle Freke itself and nearby Rathbarry Castle, a manor house about a field over. The main castle extends to some 4,645 square metres &mdash; around 50,000 square feet &mdash; though the restoration has concentrated on roughly half of that.
        </p>
        <p className="article-p">
          More than 40 chimneys have been relined. The battlements, knocked for their lead in the 1950s, have been repaired, along with many external walls. But it is in the decorative plasterwork where the most dramatic changes can be seen.
        </p>

        <h2 className="article-h2">Master craftsmen at work</h2>
        <p className="article-p">
          The principal stuccodores, Kevin Holbrook and Dolcie Keogh, have spent years creating fan-vaulted ceilings, Solomonic columns, and decorative friezes of extraordinary detail. Their work draws on the high point of Irish Georgian decorative art, when newly wealthy landowners brought in Italian craftsmen to decorate their interiors.
        </p>
        <p className="article-p">
          &ldquo;The Irish brought a level of creativity and whimsy to it,&rdquo; Evans-Freke says. &ldquo;I wanted it to be authentically Irish, not bound by the classicism of that time.&rdquo;
        </p>
        <p className="article-p">
          In recognition of their work, Holbrook and Keogh were recently appointed master plasterers of The Worshipful Company of Plasterers, a 500-year-old City of London livery guild. Keogh is the first woman master plasterer in the company&rsquo;s long history.
        </p>

        <h2 className="article-h2">Stories in stone and stucco</h2>
        <p className="article-p">
          The decorative work tells the family&rsquo;s story across cultures. In the ballroom, stucco reliefs depict the Lady of the Lake presenting Arthur with Excalibur &mdash; a nod to Evans-Freke&rsquo;s Welsh ancestry. In the saloon, the Norse god Odin stands with his two wolves, one of whom is called Freke, honouring the family&rsquo;s Viking lineage.
        </p>
        <p className="article-p">
          On the grand staircase, Evans-Freke himself appears in plaster &mdash; as a rotund mole holding a martini glass. Above, the Children of Lir fly in relief, representing his Irish heritage. It is a building that is simultaneously grand and deeply personal.
        </p>

        <h2 className="article-h2">A labour of love</h2>
        <p className="article-p">
          Describing it as &ldquo;a pay-as-you-go project&rdquo; with no grant aid, Evans-Freke estimates he has spent between &euro;10 and &euro;20 million over twenty years. Utilities are yet to be installed. The bedroom floor and half-basement need completion. Another three years of work remains on the main block.
        </p>
        <p className="article-p">
          &ldquo;There&rsquo;s no way that you can recapture that money purely from a financial point of view,&rdquo; he concedes. &ldquo;To be clear, I never saw this as a &lsquo;financial investment&rsquo;. It&rsquo;s more about heritage, an opportunity to leave behind something for future generations, a rare privilege.&rdquo;
        </p>
        <p className="article-p">
          &ldquo;Our time here is pretty fleeting, and it gives me a deep, deep satisfaction that I did something with my money that will outlast me.&rdquo;
        </p>
      </article>

      {/* Timeline */}
      <div className="castle-timeline-wrap">
        <div className="castle-timeline" data-testid="castle-timeline">
          <h2 className="article-h2" style={{ textAlign: "center", marginBottom: "3rem" }}>Timeline</h2>
          {TIMELINE.map((t, i) => (
            <div key={i} className="timeline-item" data-testid={`timeline-${i}`}>
              <span className="timeline-year">{t.year}</span>
              <span className="timeline-event">{t.event}</span>
            </div>
          ))}
        </div>
      </div>

      <div className="article-footer">
        <Link to="/" className="article-footer-link" data-testid="castle-footer-back">&larr; Back to Castlefreke &amp; Long Strand</Link>
      </div>
    </div>
  );
}
